import { ChevronLeft, Layers } from "lucide-react";
import type { Job } from "../types";
import {
  countJobsByDossierType,
  DOSSIER_TYPE_FILTER_LABELS,
  JOB_CATEGORY_LABELS,
} from "../utils/jobCategories";
import type { DossierTypeFilter, JobCategory } from "../utils/jobCategories";

export interface JobsDossierTypeHubProps {
  jobs: Job[];
  category: JobCategory;
  accent?: "indigo" | "blue";
  layout?: "grid" | "rows";
  onSelect: (type: DossierTypeFilter) => void;
  onBack: () => void;
}

export function JobsDossierTypeHub({
  jobs,
  category,
  accent = "indigo",
  layout = "grid",
  onSelect,
  onBack,
}: JobsDossierTypeHubProps) {
  const entries = countJobsByDossierType(jobs);
  const ringAccent =
    accent === "blue"
      ? "focus-visible:ring-blue-500/40"
      : "focus-visible:ring-indigo-500/40";
  const backAccent =
    accent === "blue"
      ? "text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
      : "text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300";
  const isRows = layout === "rows";

  return (
    <section className="space-y-3" aria-label="Types de dossiers">
      <div className="flex flex-wrap items-center gap-3 px-0.5">
        <button
          type="button"
          onClick={onBack}
          className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${backAccent}`}
        >
          <ChevronLeft size={18} />
          Catégories
        </button>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white tracking-tight">
          {JOB_CATEGORY_LABELS[category]}
          <span className="ml-2 text-sm font-normal text-gray-500 dark:text-slate-400">
            — choisissez un type
          </span>
        </h2>
      </div>
      <p className="text-sm text-gray-500 dark:text-slate-400 px-0.5 leading-snug">
        Les dossiers ne sont plus mélangés : un type à la fois
      </p>
      {entries.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800/80 px-4 py-10 text-center text-sm text-gray-500 dark:text-slate-400">
          Aucun dossier dans cette catégorie
        </div>
      ) : (
        <div
          className={
            isRows
              ? "flex flex-col gap-2.5"
              : "grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
          }
        >
          {entries.map(({ type, label, count }) => {
            const isNone = type === "__none__";
            const cardClass = isNone
              ? "from-slate-50 to-gray-50/80 border-gray-200/80 dark:from-slate-800/60 dark:to-slate-900/40 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20"
              : "from-violet-50 to-indigo-50/80 border-violet-200/70 dark:from-violet-500/10 dark:to-indigo-500/5 dark:border-violet-500/25 hover:border-violet-300 dark:hover:border-violet-400/40";
            const iconClass = isNone
              ? "text-gray-600 dark:text-slate-300 bg-gray-100/80 dark:bg-slate-700/60"
              : "text-violet-600 dark:text-violet-400 bg-violet-100/80 dark:bg-violet-500/20";
            const badgeClass = isNone
              ? "bg-gray-700 dark:bg-slate-500"
              : "bg-violet-600 dark:bg-violet-500";

            if (isRows) {
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => onSelect(type)}
                  aria-label={`${label}, ${count} dossier${count !== 1 ? "s" : ""}`}
                  className={`group w-full flex items-center gap-3 text-left p-3.5 rounded-2xl border bg-gradient-to-br shadow-sm transition-all duration-200 active:scale-[0.99] focus:outline-none focus-visible:ring-2 ${ringAccent} ${cardClass}`}
                >
                  <div
                    className={`flex items-center justify-center w-11 h-11 rounded-xl shrink-0 ${iconClass}`}
                  >
                    <Layers size={22} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0 py-0.5">
                    <span className="block text-[15px] font-semibold text-gray-900 dark:text-white tracking-tight leading-tight">
                      {label}
                    </span>
                    <span className="block text-[12px] text-gray-500 dark:text-slate-400 mt-0.5">
                      {DOSSIER_TYPE_FILTER_LABELS[type]}
                    </span>
                  </div>
                  <span
                    className={`shrink-0 min-w-[2rem] h-8 px-2.5 inline-flex items-center justify-center rounded-full text-[13px] font-bold text-white tabular-nums shadow-sm ${badgeClass}`}
                  >
                    {count}
                  </span>
                </button>
              );
            }

            return (
              <button
                key={type}
                type="button"
                onClick={() => onSelect(type)}
                aria-label={`${label}, ${count} dossier${count !== 1 ? "s" : ""}`}
                className={`group relative flex flex-col items-start text-left p-4 md:p-5 pr-12 rounded-2xl border bg-gradient-to-br shadow-sm transition-all duration-200 active:scale-[0.98] focus:outline-none focus-visible:ring-2 ${ringAccent} ${cardClass}`}
              >
                <div
                  className={`flex items-center justify-center w-11 h-11 rounded-xl mb-3 shrink-0 ${iconClass}`}
                >
                  <Layers size={22} strokeWidth={2} />
                </div>
                <span className="w-full text-[13px] sm:text-[15px] md:text-base font-semibold text-gray-900 dark:text-white tracking-tight leading-snug break-words">
                  {label}
                </span>
                <span className="w-full text-[11px] sm:text-[12px] text-gray-500 dark:text-slate-400 mt-1 leading-snug">
                  {count} dossier{count !== 1 ? "s" : ""}
                </span>
                <span
                  className={`absolute top-3 right-3 min-w-[1.75rem] h-7 px-2 inline-flex items-center justify-center rounded-full text-[12px] font-bold text-white tabular-nums shadow-sm ${badgeClass}`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
